import { DbBrokerInvoice, isBrokerInvoiceStageableForFactoringBatch } from '@numeo/types-core'

interface InvoiceBatchCandidate {
    batchName: string | null | undefined
    isNamedBatch: boolean
    invoices: DbBrokerInvoice[]
}

export const canSubmitInvoiceBatchToFactoring = (batch: Pick<InvoiceBatchCandidate, 'invoices'>): boolean =>
    batch.invoices.some(isBrokerInvoiceStageableForFactoringBatch)

export const canAppendToInvoiceBatch = (batch: InvoiceBatchCandidate): boolean =>
    Boolean(batch.batchName) && batch.isNamedBatch && batch.invoices.length > 0 && batch.invoices.every(isBrokerInvoiceStageableForFactoringBatch)

/**
 * What taking invoices out of a batch does to each, which the server decides by how it got into the batch: an invoice that was grouped into it after it
 * existed (`regroupedIntoBatch`) is only taken out and stays on the Invoiced page; one created with the batch, from Ready to Invoice, is undone and its load
 * goes back to Ready to Invoice. The dialog says which, instead of listing both for the dispatcher to work out.
 */
export const describeBatchRemoval = (
    invoices: readonly Pick<DbBrokerInvoice, 'regroupedIntoBatch'>[],
    // The moment the sentence is about: an invoice taken out of the batch, or the whole batch undone (which treats each of its invoices the same way).
    action: { title: string; when: string } = { title: 'Remove from batch?', when: 'removed from the batch' }
): { title: string; message: string } | null => {
    // Nothing found to describe: the caller keeps its general wording rather than a sentence about "0 loads".
    if (invoices.length === 0) return null
    const { title, when } = action
    const kept = invoices.filter((invoice) => invoice.regroupedIntoBatch).length
    const undone = invoices.length - kept
    const loads = (count: number) => (count === 1 ? 'This load' : `These ${count} loads`)
    const toReady = (count: number) =>
        `${loads(count)} will go back to "Ready to Invoice" when ${when}. ${count === 1 ? 'Its invoice is' : 'Their invoices are'} deleted.`
    const stays = (count: number) => `${count === 1 ? 'This invoice' : `These ${count} invoices`} will stay on the Invoiced page when ${when}.`
    if (kept === 0) return { title, message: toReady(undone) }
    if (undone === 0) return { title, message: stays(kept) }
    return {
        title,
        message: `${undone} load${undone === 1 ? '' : 's'} will go back to "Ready to Invoice" (${undone === 1 ? 'its invoice is' : 'their invoices are'} deleted); ${kept} invoice${kept === 1 ? '' : 's'} will stay on the Invoiced page.`,
    }
}
